import React, { useState } from 'react'
import ReportLayout from '../../layouts/Report'

import useGetSchools from '../../../hooks/schools/useGetSchools'
import useGetCampuses from '../../../hooks/campuses/useGetCampuses'
import useGetUserJobsReport from '../../../hooks/analytics/useUserJobsReport'
import useExportUserJobsReport from '../../../hooks/analytics/useExportUserJobsReport'
import { useGetStudentOnboardingResponsesOptionsByField } from '../../../hooks/misc'

import BarChart from '../../sub_components/charts/BarChart'
import ReportDatePicker from '../../sub_components/ReportDatePicker'
import FilterTable from '../../sub_components/FilterTable'
import ReportTablet from '../../sub_components/ReportTablet'

import { IColumnProps, IFilterableDataProps } from '../../../interfaces/table'
import { StudentOnboardingResponseOptionsField } from '../../../interfaces/types'
import { IChartData } from '../../../interfaces/charts'

const UserJobsReport = () => {
    const [schoolFilters, setSchoolFilters] = useState<number[]>([]);
    const [campusFilters, setCampusFilters] = useState<number[]>([]);
    const [programsOfStudyFilters, setProgramsOfStudyFilters] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const { data: programsOfStudy } = useGetStudentOnboardingResponsesOptionsByField(StudentOnboardingResponseOptionsField.PROGRAM_OF_STUDY);

    const programsOfStudyOptions = programsOfStudy?.map((program, index) => ({
        id: index,
        name: program,
    })) ?? [];


    const filterQuery = new URLSearchParams();
    filterQuery.set("start_date", startDate);
    filterQuery.set("end_date", endDate);
    schoolFilters.forEach((id) => filterQuery.append("school_ids", id.toString()));
    campusFilters.forEach((id) => filterQuery.append("campus_ids", id.toString()));
    programsOfStudyFilters.forEach((id) => filterQuery.append("programs_of_study", programsOfStudyOptions[id].name));

    const { data: schools } = useGetSchools();
    const { data: campuses } = useGetCampuses();
    const { data: userJobsReport } = useGetUserJobsReport(filterQuery.toString());

    const { mutate: exportCsv } = useExportUserJobsReport();

    let filterTableDataColumns__Schools: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "School Name", identifier: "name" },
    ];
    let filterTableDataColumns__Campuses: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Campus Name", identifier: "name" },
    ];
    let filterTableDataColumns__ProgramsOfStudy: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Program of Study", identifier: "name" },
    ];
    let filterTableDataColumns__UserJobsReport: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Full Name", identifier: "full_name" },
        { name: "Email", identifier: "email" },
        { name: "Visits", identifier: "visits" },
    ];

    let filterTableData__Schools: IFilterableDataProps[] = [];
    let filterTableData__Campuses: IFilterableDataProps[] = [];
    let filterTableData__ProgramsOfStudy: IFilterableDataProps[] = [];
    let filterTableData__UserJobsReport: IFilterableDataProps[] = [];

    if (schools) {
        filterTableData__Schools = schools.map((school) => ({
            id: school.id,
            name: school.name,
        }));
    }

    if (campuses) {
        filterTableData__Campuses = campuses.map((campus) => ({
            id: campus.id,
            name: campus.name,
        }));
    }

    if (programsOfStudy) {
        filterTableData__ProgramsOfStudy = programsOfStudyOptions.map((program) => ({
            id: program.id,
            name: program.name,
        }));
    }

    if (userJobsReport) {
        filterTableData__UserJobsReport = userJobsReport.table_data.map((user) => ({
            id: user.id,
            full_name: user.full_name,
            email: user.email,
            visits: user.visits,
        }));
    }

    const trimmedChartDataLabels = (data: IChartData[]): IChartData[] => {
        return data.map((item) => item.name.length > 5 ? { ...item, name: item.name.slice(0, 5) + '...' } : item);
    }

    const onExportCsvReport = () => {
        exportCsv({
            payload: {
                filterQuery: filterQuery.toString(),
            }
        });
    };

    return (
        <ReportLayout>
            <div className='immersion_admin__careers_report'>
                <div className='immersion_admin__careers_report__header'>
                    <div className='immersion_admin__careers_report__header__content'>
                        <h2 className='immersion_admin__careers_report__header__content__title'>User Jobs Report</h2>
                        <p className='immersion_admin__careers_report__header__content__description'>
                            This slide gives an overview of user job visits.
                        </p>
                    </div>
                    <div className='immersion_admin__careers_report__header__actions'>
                        <button className='immersion_admin__btn__primary immersion_admin__careers_report__header__actions__button' onClick={onExportCsvReport}>Export CSV</button>
                    </div>
                </div>

                <div className='immersion_admin__careers_report__chart_body'>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__Schools}
                            data={filterTableData__Schools}
                            enableFilters={true}
                            filters={schoolFilters}
                            setFilters={setSchoolFilters}
                        />
                        <FilterTable
                            columns={filterTableDataColumns__Campuses}
                            data={filterTableData__Campuses}
                            enableFilters={true}
                            filters={campusFilters}
                            setFilters={setCampusFilters}
                        />
                        <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__vertical'>
                            <ReportDatePicker
                                required={true}
                                startDate={startDate}
                                setStartDate={setStartDate}
                                endDate={endDate}
                                setEndDate={setEndDate}
                            />
                            <div className='immersion_admin__careers_report__chart__row'>
                                <ReportTablet
                                    title={userJobsReport?.total_visits?.toString() ?? "0"}
                                    description='Total Visits'
                                    color={'green'}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__vertical'>
                            <FilterTable
                                columns={filterTableDataColumns__ProgramsOfStudy}
                                data={filterTableData__ProgramsOfStudy}
                                enableFilters={true}
                                filters={programsOfStudyFilters}
                                setFilters={setProgramsOfStudyFilters}
                            />
                            <FilterTable
                                columns={filterTableDataColumns__UserJobsReport}
                                data={filterTableData__UserJobsReport}
                                enableFilters={false}
                                filters={[]}
                                setFilters={() => { }}
                            />
                        </div>
                        {userJobsReport?.job_post_chart_data.length ? (
                            <BarChart
                                width={600}
                                height={318}
                                data={trimmedChartDataLabels(userJobsReport?.job_post_chart_data ?? [])}
                                xLabel="Job Post"
                                yLabel="No. of Visits"
                                xInterval={4}
                            />
                        ) : (
                            <div className='immersion_admin__careers_report__chart__no_data'>
                                No analytics data available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ReportLayout>
    )
}

export default UserJobsReport;